import { formatTime, platformOffsetMinutes } from '@simpleacs/shared';
/**
 * The axes under the Traffic card's trace — where the gridlines and their
 * labels go, decided in the data domain so the card only has to draw them.
 * Pure and DOM-free like `traffic-curve.ts`, and for the same reason: a tick
 * that lands off the scale, or a label that says "12:00" for a window that
 * ends at 11:58, is the kind of thing a node test catches and a glance at a
 * screenshot does not.
 *
 *  · `rateTicks` picks the rate gridlines for a scale whose top is `peak`
 *    (bytes per second, the card's unit): nice numbers in BITS — 1, 2, 2.5,
 *    5 × 10ⁿ — because the label reads "20 Mb/s", never "2.6 MB/s".
 *  · `timeTicks` picks the clock marks for a window [t0, t1]: whole
 *    multiples of a step chosen from the span (30 s for the live minutes,
 *    30 min for the static three hours, …), so the labels are round times
 *    that slide under a live trace rather than a fixed set that jumps.
 *  · `compactBitRate` is the axis label: short, one unit, no decimals where
 *    a round tick has none.
 */

/** A rate label short enough for a 46 px gutter: "0", "500 kb/s", "24 Mb/s", "1.2 Gb/s". */
export function compactBitRate(bytesPerSecond: number): string {
  if (!Number.isFinite(bytesPerSecond) || bytesPerSecond <= 0) return '0';
  const units = ['b/s', 'kb/s', 'Mb/s', 'Gb/s'] as const;
  let value = bytesPerSecond * 8;
  let i = 0;
  while (value >= 1000 && i < units.length - 1) {
    value /= 1000;
    i += 1;
  }
  const rounded = Math.round(value * 10) / 10;
  const text = Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
  return `${text} ${units[i]}`;
}

/** The nice step (1, 2, 2.5 or 5 × 10ⁿ) at or above `raw`. */
export function niceStep(raw: number): number {
  if (!Number.isFinite(raw) || raw <= 0) return 1;
  const exponent = Math.floor(Math.log10(raw));
  const base = 10 ** exponent;
  const fraction = raw / base;
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10;
  return nice * base;
}

/**
 * Rate gridlines for a scale topped by `peak` bytes/s: at most `want` of
 * them, ascending, all strictly inside (0, peak]. The zero line is the
 * card's own and is not repeated here. An idle link (peak at the floor of 1)
 * gets no gridline at all rather than one for "8 b/s".
 */
export function rateTicks(peak: number, want = 3): number[] {
  if (!Number.isFinite(peak) || peak <= 1) return [];
  const peakBits = peak * 8;
  const step = niceStep(peakBits / want);
  const ticks: number[] = [];
  for (let v = step; v <= peakBits * 1.0001 && ticks.length < want; v += step) {
    ticks.push(v / 8);
  }
  return ticks;
}

/** The clock step for a window of `spanMs`: round marks, never more than about eight of them. */
export function timeStepMs(spanMs: number): number {
  const steps = [
    10_000, 30_000, 60_000, 2 * 60_000, 5 * 60_000, 10 * 60_000, 15 * 60_000, 30 * 60_000,
    3_600_000, 2 * 3_600_000, 3 * 3_600_000, 6 * 3_600_000, 12 * 3_600_000, 24 * 3_600_000,
  ];
  for (const step of steps) if (spanMs / step <= 8) return step;
  return steps[steps.length - 1]!;
}

/**
 * Clock marks inside [t0, t1]: every whole multiple of the step from
 * `timeStepMs` that falls in the window. `withSeconds` says whether the
 * labels need seconds — they do when the step is under a minute.
 */
export function timeTicks(t0: number, t1: number): { at: number[]; stepMs: number; withSeconds: boolean } {
  if (!Number.isFinite(t0) || !Number.isFinite(t1) || t1 <= t0) return { at: [], stepMs: 0, withSeconds: false };
  const stepMs = timeStepMs(t1 - t0);
  // Round on the platform's clock, not UTC's: a 6 h step lands on 00/06/12/18 EAT.
  const offset = platformOffsetMinutes(t0) * 60_000;
  const at: number[] = [];
  for (let t = Math.ceil((t0 + offset) / stepMs) * stepMs - offset; t <= t1; t += stepMs) {
    if (t >= t0) at.push(t);
  }
  return { at, stepMs, withSeconds: stepMs < 60_000 };
}

/** "13:05" or "13:05:30", on the platform's clock (EAT). */
export function clockLabel(at: number, withSeconds: boolean): string {
  return formatTime(at, { seconds: withSeconds });
}
